import React, { useState, useEffect, useRef } from 'react'
import { Card, Flex, Text, Button, Table, Badge, ScrollArea } from '@radix-ui/themes'
import { DownloadIcon, RocketIcon, MagnifyingGlassIcon, ReloadIcon, ChevronDownIcon } from '@radix-ui/react-icons'
import { api } from '@/utils/api'
import { useToast } from '@/components/ToastProvider'
import { useAuth } from '@/hooks/useAuth'

interface Org {
  _id: string
  name: string
}

const REPORT_TYPES = [
  { id: 'deals', title: 'Negocios', desc: 'Datos completos de tus negocios vigentes.' },
  { id: 'clients', title: 'Directorio de Clientes', desc: 'Lista limpia de clientes y datos de contacto.' },
  { id: 'pending-margins', title: 'Margen Notas de Venta', desc: 'Margen de notas de ventas pendientes.' },
  { id: 'messages', title: 'Historial Comunicaciones', desc: 'Registro de auditoría de mensajes por negocio.' },
  { id: 'archived-deals', title: 'Negocios Cancelados', desc: 'Análisis de negocios archivados y cancelados.' },
]

// Download filename slugs, where they differ from the report id.
// Falls back to the id, so new report types need no entry here.
const REPORT_FILENAMES: Record<string, string | undefined> = {
  deals: 'negocios',
}

export const AdvancedReports: React.FC = () => {
  const { showToast } = useToast()
  const { userData } = useAuth()
  const ALLOWED_EMAILS = ['jpherrera@veekls.com', 'ashu@veekls.com']
  const isSuperAdmin = userData?.user?.email && ALLOWED_EMAILS.includes(userData.user.email) && userData?.organization?.name.toLowerCase().includes('demo')

  const [orgs, setOrgs] = useState<Org[]>([])
  const [filteredOrgs, setFilteredOrgs] = useState<Org[]>([])
  const [orgSearch, setOrgSearch] = useState<string>('')
  const [selectedOrg, setSelectedOrg] = useState<string>('')
  const [selectedReport, setSelectedReport] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [previewData, setPreviewData] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    // Fetch all orgs
    const fetchOrgs = async () => {
      if (!isSuperAdmin) {
        // If standard user, set selected to their own org immediately
        if (userData?.organization?._id) {
            setSelectedOrg(userData.organization._id)
        }
        return
      }
      try {
        const response = await api.get('reports/organizations')
        if (response.data) {
          setOrgs(response.data)
          setFilteredOrgs(response.data)
          // Auto-select first org or demo
          const demo = response.data.find((o: Org) => o.name.toLowerCase().includes('demo'))
          if (demo) setSelectedOrg(demo._id)
        }
      } catch (err) {
        console.error('Failed to load orgs', err)
      }
    }
    fetchOrgs()
  }, [isSuperAdmin, userData])

  useEffect(() => {
    if (orgSearch.trim() === '') {
      setFilteredOrgs(orgs)
    } else {
      const lowerSearch = orgSearch.toLowerCase()
      setFilteredOrgs(orgs.filter((o) => o.name.toLowerCase().includes(lowerSearch)))
    }
  }, [orgSearch, orgs])

  useEffect(() => {
    // Reset report and dates when org changes
    setSelectedReport('')
    setStartDate('')
    setEndDate(new Date().toISOString().split('T')[0])
    setPreviewData([])
    setTotalCount(0)
  }, [selectedOrg])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (selectedOrg && selectedReport) {
      loadPreview()
    } else {
      setPreviewData([])
      setTotalCount(0)
    }
  }, [selectedOrg, selectedReport, startDate, endDate])

  const loadPreview = async () => {
    setLoadingPreview(true)
    try {
      const activeOrgStr = userData?.organization?._id || ''
      const targetOrg = isSuperAdmin ? selectedOrg : activeOrgStr

      let url = `reports/${selectedReport}?targetOrgId=${targetOrg}&preview=true${activeOrgStr ? `&activeOrgId=${activeOrgStr}` : ''}`
      if (startDate) url += `&startDate=${startDate}`
      if (endDate) url += `&endDate=${endDate}`

      const res = await api.get(url)
      if (res && res.data) {
        setPreviewData(res.data.data)
        setTotalCount(res.data.totalCount)
      }
    } catch (err) {
      showToast('Error cargando la vista previa', 'error')
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleDownload = async () => {
    if (!selectedReport) return
    if (isSuperAdmin && !selectedOrg) return

    setDownloading(true)
    try {
      const activeOrgStr = userData?.organization?._id || ''
      const targetOrg = isSuperAdmin ? selectedOrg : activeOrgStr

      let url = `reports/${selectedReport}?targetOrgId=${targetOrg}${activeOrgStr ? `&activeOrgId=${activeOrgStr}` : ''}`
      if (startDate) url += `&startDate=${startDate}`
      if (endDate) url += `&endDate=${endDate}`

      // Request as blob to inject Authorization header via interceptor
      const response = await api.get(url, { responseType: 'blob' })

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const downloadUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', `Reporte-${REPORT_FILENAMES[selectedReport] ?? selectedReport}.xlsx`)
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      showToast('Descarga exitosa', 'success')
    } catch (err) {
      console.error('Download error:', err)
      showToast('Error al descargar', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 w-full py-4">
      {/* Configuration Panel */}
      <div className="w-full lg:w-1/3 flex flex-col gap-4">
        <Card size="2" className="bg-slate-50 dark:bg-slate-900 shadow-md">
          <Flex direction="column" gap="4">
            <Text size="5" weight="bold" className="text-blue-600 dark:text-blue-400">
              Configuración de Reporte
            </Text>

            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                Organización
              </Text>

                            {isSuperAdmin ? (
                <div ref={dropdownRef} className="relative w-full">
                  {!isDropdownOpen ? (
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(true)}
                      className="w-full flex items-center justify-between border rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span className="truncate">
                        {selectedOrg ? orgs.find((o) => o._id === selectedOrg)?.name : 'Seleccionar Organización...'}
                      </span>
                      <ChevronDownIcon className="text-gray-500" />
                    </button>
                  ) : (
                    <div className="absolute top-0 left-0 w-full z-10 border rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden">
                      <Flex align="center" className="px-2 py-1 border-b border-gray-200 dark:border-gray-800">
                        <MagnifyingGlassIcon className="text-gray-400 mr-2" />
                        <input
                          type="text"
                          autoFocus
                          placeholder="Buscar organización..."
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                          className="w-full text-sm outline-none bg-transparent p-1"
                        />
                      </Flex>
                      <ScrollArea type="auto" style={{ maxHeight: '200px' }}>
                        <div className="flex flex-col p-1">
                          {filteredOrgs.length > 0 ? (
                            filteredOrgs.map((org) => (
                              <div
                                key={org._id}
                                onClick={() => {
                                  setSelectedOrg(org._id)
                                  setIsDropdownOpen(false)
                                  setOrgSearch('')
                                }}
                                className={`px-3 py-2 text-sm cursor-pointer rounded-md transition-colors ${
                                  selectedOrg === org._id
                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                              >
                                {org.name}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500 text-center">
                              No se encontraron organizaciones
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full border rounded-md border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-slate-900 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 cursor-not-allowed">
                  <span className="truncate">{userData?.organization?.name || 'Tu Organización'}</span>
                </div>
              )}
            </Flex>

            <Flex gap="3">
              <Flex direction="column" gap="1" className="flex-1">
                <Text size="2" weight="bold">
                  Desde
                </Text>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border rounded p-1.5 text-sm w-full dark:bg-slate-800 dark:border-slate-700"
                />
              </Flex>
              <Flex direction="column" gap="1" className="flex-1">
                <Text size="2" weight="bold">
                  Hasta
                </Text>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border rounded p-1.5 text-sm w-full dark:bg-slate-800 dark:border-slate-700"
                />
              </Flex>
            </Flex>
          </Flex>
        </Card>

        <Flex direction="column" gap="3">
          <Text size="4" weight="bold" className="mb-2">
            Seleccionar Tipo
          </Text>
          {REPORT_TYPES.map((report) => (
            <Card
              key={report.id}
              size="1"
              className={`cursor-pointer transition-all hover:border-blue-500 hover:shadow-md ${selectedReport === report.id ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500/50' : ''}`}
              onClick={() => setSelectedReport(report.id)}
            >
              <Flex direction="column" gap="1">
                <Text size="3" weight="bold">
                  {report.title}
                </Text>
                <Text size="1" color="gray">
                  {report.desc}
                </Text>
              </Flex>
            </Card>
          ))}
        </Flex>
      </div>

      {/* Preview and Action Panel */}
      <div className="w-full lg:w-2/3 flex flex-col gap-4">
        <Card
          size="3"
          className="flex-1 shadow-lg bg-white dark:bg-slate-900 border-t-4 border-t-blue-500 min-h-[500px]"
        >
          <Flex direction="column" height="100%" gap="4">
            <Flex justify="between" align="center" className="border-b pb-4">
              <Flex align="center" gap="2">
                <MagnifyingGlassIcon width="24" height="24" className="text-blue-500" />
                <Text size="5" weight="bold">
                  Vista Previa
                </Text>
                {totalCount > 0 && (
                  <Badge color="blue" variant="soft" className="ml-2">
                    {totalCount} Registros
                  </Badge>
                )}
              </Flex>

              <Button
                size="3"
                variant="solid"
                color="green"
                disabled={!selectedReport || totalCount === 0 || downloading}
                onClick={handleDownload}
                className="shadow-md hover:shadow-lg transition-all"
              >
                {downloading ? <ReloadIcon className="animate-spin" /> : <DownloadIcon />}
                Descargar Excel (.xlsx)
              </Button>
            </Flex>

            {loadingPreview ? (
              <Flex align="center" justify="center" className="flex-1 h-64">
                <Flex direction="column" align="center" gap="3">
                  <ReloadIcon className="animate-spin text-blue-500" width="32" height="32" />
                  <Text color="gray">Cargando datos...</Text>
                </Flex>
              </Flex>
            ) : !selectedReport ? (
              <Flex
                align="center"
                justify="center"
                className="flex-1 h-64 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg"
              >
                <Flex direction="column" align="center" gap="2">
                  <RocketIcon width="48" height="48" className="text-gray-300" />
                  <Text size="3" color="gray" weight="medium">
                    Selecciona un reporte a la izquierda
                  </Text>
                </Flex>
              </Flex>
            ) : previewData.length === 0 ? (
              <Flex align="center" justify="center" className="flex-1 h-64">
                <Text color="gray">No hay datos para esta selección.</Text>
              </Flex>
            ) : (
              <ScrollArea type="auto" scrollbars="both" style={{ height: '400px' }}>
                <Table.Root variant="surface" className="w-full text-sm">
                  <Table.Header>
                    <Table.Row>
                      {Object.keys(previewData[0]).map((key) => (
                        <Table.ColumnHeaderCell key={key} className="whitespace-nowrap">
                          {key}
                        </Table.ColumnHeaderCell>
                      ))}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {previewData.map((row, i) => (
                      <Table.Row key={i}>
                        {Object.values(row).map((val: any, j) => (
                          <Table.Cell key={j} className="whitespace-nowrap truncate max-w-[200px]" title={String(val)}>
                            {String(val)}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </ScrollArea>
            )}

            {totalCount > 10 && (
              <Text size="2" color="gray" align="center" className="mt-2">
                Mostrando 10 de {totalCount} registros. Descarga el archivo para ver todos.
              </Text>
            )}
          </Flex>
        </Card>
      </div>
    </div>
  )
}
